import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ScheduledPostsList = ({ posts, onEditPost, onDeletePost, onDuplicatePost }) => {
  const platformIcons = {
    facebook: { icon: 'Facebook', color: 'text-blue-600' },
    instagram: { icon: 'Instagram', color: 'text-pink-600' },
    twitter: { icon: 'Twitter', color: 'text-sky-500' },
    linkedin: { icon: 'Linkedin', color: 'text-blue-700' },
    tiktok: { icon: 'Music', color: 'text-gray-900' }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-accent/10 text-accent';
      case 'published':
        return 'bg-success/10 text-success';
      case 'failed':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDateTime = (date) => {
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sortedPosts = [...posts]?.sort((a, b) => a?.scheduledDate - b?.scheduledDate);

  if (posts?.length === 0) {
    return (
      <div className="text-center py-12">
        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Scheduled Posts</h3>
        <p className="text-sm text-muted-foreground">
          Create your first post to get started with content scheduling
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sortedPosts?.map(post => (
        <div
          key={post?.id}
          className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex gap-4">
            {post?.media && (
              <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                <Image
                  src={post?.media}
                  alt={post?.mediaAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  {post?.platforms?.map(platform => (
                    <div
                      key={platform}
                      className="flex items-center gap-1 px-2 py-1 rounded bg-muted"
                    >
                      <Icon
                        name={platformIcons?.[platform]?.icon}
                        size={14}
                        className={platformIcons?.[platform]?.color}
                      />
                    </div>
                  ))}
                  {post?.recurring && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-primary/10 text-primary text-xs font-medium">
                      <Icon name="Repeat" size={12} />
                      <span>Recurring</span>
                    </div>
                  )}
                </div>

                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(post?.status)}`}>
                  {post?.status?.charAt(0)?.toUpperCase() + post?.status?.slice(1)}
                </span>
              </div>

              <p className="text-sm text-foreground mb-3 line-clamp-2">
                {post?.content}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span>{formatDateTime(post?.scheduledDate)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditPost(post)}
                    iconName="Edit2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDuplicatePost(post)}
                    iconName="Copy"
                  >
                    Duplicate
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeletePost(post?.id)}
                    iconName="Trash2"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScheduledPostsList;